// The UI root: every screen, the HUD, toasts, captions, the rotate prompt and the connection
// overlay. Chooses the root screen from the app mode (boot, menu, Practice, room) and, in a room,
// from the room phase (§9.5). Also keeps `#ui` in step with the settings and the input device:
// `--ui-scale` and the viewport factor `--vp`, the reduced-motion / reduced-flashing / colorblind
// classes and `data-device` (focus rings show for keyboard and gamepad, never for touch).
import { RoomPhase } from '@shared/ids';
import type { FrameView } from '../game/FrameView';
import type { Settings } from '../profile/settings';
import './styles/base.css';
import './styles/screens.css';
import './styles/hud.css';
import './styles/menus.css';
import type { LiveScreen, ScreenId } from './contracts';
import type { UiDeps } from './deps';
import { create as createHud } from './hud/Hud';
import { createCaptions } from './hud/Captions';
import { createToasts } from './hud/Toasts';
import { ScreenManager } from './ScreenManager';
import { create as createAwards } from './screens/Awards';
import { create as createBeanCard } from './screens/BeanCard';
import { create as createBoot } from './screens/Boot';
import { createConnectionOverlay, type ConnectionState } from './screens/Connection';
import { create as createCreateRoom } from './screens/CreateRoom';
import { create as createError, type ErrorProps } from './screens/ErrorScreen';
import { create as createHowToPlay } from './screens/HowToPlay';
import { create as createJoinCode } from './screens/JoinCode';
import { create as createLobby } from './screens/Lobby';
import { create as createLocker } from './screens/Locker';
import { create as createMainMenu } from './screens/MainMenu';
import { create as createNamePrompt } from './screens/NamePrompt';
import { create as createPause } from './screens/Pause';
import { create as createPodium } from './screens/Podium';
import { create as createPractice } from './screens/Practice';
import { createRotatePrompt } from './screens/RotatePrompt';
import { create as createSettings } from './screens/Settings';
import { create as createSpectator } from './screens/Spectator';
import { create as createTally } from './screens/Tally';
import { create as createTapestry } from './screens/TapestryModal';
import { toggleClass, viewportFactor } from './ui';

/**
 * Screens during which no gameplay presses are generated (§26 `setGameplayEnabled`): every modal.
 * The menu diorama, the lobby, the in-round HUD, the spectator bar and the podium keep the bean live.
 */
/** #ui's font size at UI scale 100 % and `--vp` 1 (base.css). */
const BASE_FONT_PX = 16;
/** Below this many em of height the compact layout fits better than the desktop one. */
const COMPACT_BELOW_EM = 40;
/** Below this many em of width, the narrow (portrait-style) layout. */
const NARROW_BELOW_EM = 44;

export const GAMEPLAY_OFF_SCREENS: ReadonlySet<ScreenId> = new Set<ScreenId>([
  'boot', 'name', 'joinCode', 'createRoom', 'practice', 'pause', 'tally', 'awards', 'tapestry', 'locker', 'beanCard',
  'settings', 'howToPlay', 'error',
]);

const PHASE_SCREEN: Readonly<Record<RoomPhase, ScreenId>> = {
  [RoomPhase.LOBBY]: 'lobby', [RoomPhase.INTRO]: 'game', [RoomPhase.PLAY]: 'game', [RoomPhase.ROUND_END]: 'game',
  [RoomPhase.INTERMISSION]: 'tally', [RoomPhase.AWARDS]: 'awards', [RoomPhase.PODIUM]: 'podium',
};

/**
 * Root screens that the pause menu opens over (Esc / Start / touch PAUSE). The tally and the awards
 * too: they are not a dead end — Leave, Settings and the room link stay one press away.
 */
const PAUSABLE: ReadonlySet<ScreenId> = new Set<ScreenId>(['game', 'lobby', 'podium', 'spectator', 'tally', 'awards']);

type Mode = 'boot' | 'menu' | 'practice' | 'room';

export interface Shell {
  readonly screen: ScreenId;
  /** Every screen id on the stack, root first (debug and tests). */
  stack(): ScreenId[];
  boot(): void;
  namePrompt(): void;
  menu(): void;
  practice(): void;
  /** Follows the room phase from now on (until `menu()`). */
  room(): void;
  error(props: ErrorProps): void;
  connection(state: ConnectionState, attempt?: number): void;
  /** Opens a screen over the current one (the `?debug=ui` gallery). */
  show(id: ScreenId, props?: unknown): void;
  update(view: FrameView): void;
}

export function createShell(root: HTMLElement, base: Omit<UiDeps, 'nav'>): Shell {
  const sm = new ScreenManager(root, base.bus, { sound: (id) => base.services.playUi(id) });
  const deps: UiDeps = { ...base, nav: { show: (id, props) => sm.show(id, props), back: () => sm.back(), current: () => sm.current } };
  const screens = new Map<ScreenId, LiveScreen>([
    ['boot', createBoot(deps)], ['name', createNamePrompt(deps)], ['menu', createMainMenu(deps)],
    ['joinCode', createJoinCode(deps)], ['createRoom', createCreateRoom(deps)], ['practice', createPractice(deps)],
    ['lobby', createLobby(deps)], ['game', createHud(deps)], ['spectator', createSpectator(deps)],
    ['tally', createTally(deps)], ['awards', createAwards(deps)], ['podium', createPodium(deps)],
    ['tapestry', createTapestry(deps)], ['pause', createPause(deps)], ['locker', createLocker(deps)],
    ['beanCard', createBeanCard(deps)], ['settings', createSettings(deps)], ['howToPlay', createHowToPlay(deps)],
    ['error', createError(deps)],
  ]);
  for (const [id, s] of screens) {
    s.el.hidden = true;
    s.el.dataset.screen = id;
    sm.register(id, s);
  }
  // §11.5 UI sounds: a hover tick and a click pluck on every generic button (cards that mean
  // something — votes, mutators, items — play their own sound).
  const SOUNDED = '.btn, .tile, .tab, .player-card';
  let hovered: Element | null = null;
  let hoverAt = 0;
  root.addEventListener('pointerover', (e) => {
    if (e.pointerType === 'touch' || !(e.target instanceof Element)) return;
    const t = e.target.closest(SOUNDED);
    if (t === hovered) return;
    hovered = t;
    const now = performance.now();
    if (!t || (t as HTMLButtonElement).disabled || now - hoverAt < 45) return;
    hoverAt = now;
    base.services.playUi('hover');
  });
  root.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target.closest(SOUNDED) : null;
    if (t && !(t as HTMLButtonElement).disabled) base.services.playUi('click');
  });
  // Scroll affordance: a sheet whose body scrolls fades the edge where more content waits, so a
  // cut-off row reads as "scroll for more" on a short screen, not as a layout bug.
  function syncScrollFade(body: HTMLElement): void {
    const more = body.scrollHeight - body.clientHeight;
    toggleClass(body, 'more-above', more > 2 && body.scrollTop > 2);
    toggleClass(body, 'more-below', more > 2 && body.scrollTop < more - 2);
  }
  /** The same for a tab strip that scrolls sideways (Settings on a phone). */
  function syncTabFade(strip: HTMLElement): void {
    const more = strip.scrollWidth - strip.clientWidth;
    toggleClass(strip, 'more-left', more > 2 && strip.scrollLeft > 2);
    toggleClass(strip, 'more-right', more > 2 && strip.scrollLeft < more - 2);
  }
  root.addEventListener('scroll', (e) => {
    if (!(e.target instanceof HTMLElement)) return;
    if (e.target.classList.contains('sheet-body')) syncScrollFade(e.target);
    else if (e.target.classList.contains('tabs')) syncTabFade(e.target);
  }, { capture: true, passive: true });
  let fadeCheckAt = 0;
  const overlay = createConnectionOverlay(deps);
  const rotate = createRotatePrompt(deps);
  const captions = createCaptions(deps);
  root.append(captions.el, rotate.el, overlay.el, createToasts(deps));

  let mode: Mode = 'boot';
  let roomRoot: ScreenId | null = null;

  function followRoom(): void {
    if (mode !== 'room') return;
    const phase = deps.room.phase;
    if (phase === null) return;
    const target = PHASE_SCREEN[phase];
    if (target === roomRoot) return;
    roomRoot = target;
    sm.replace(target);
  }
  deps.room.onChange(followRoom);

  /** The spectator bar sits over the HUD while the local player only watches (§9.6, §14.2). */
  function syncSpectator(): void {
    const want = mode === 'room' && sm.rootId === 'game' && deps.services.spectating();
    if (want && !sm.has('spectator') && sm.current === 'game') sm.show('spectator');
    else if (!want && sm.has('spectator')) sm.remove('spectator');
  }

  base.bus.on('input:nav', ({ action }) => {
    if (action !== 'pause') return;
    if (mode === 'menu') {
      // Esc with nothing focused in the menu: bring keyboard focus back to the menu.
      if (sm.current === 'menu') sm.focusCurrent();
      else sm.back();
      return;
    }
    if (mode !== 'room' && mode !== 'practice') return;
    if (sm.current === 'pause') sm.back();
    else if (PAUSABLE.has(sm.current)) sm.show('pause');
    else if (sm.depth > 1) sm.back();
  });

  // ---- #ui state: scale, device, accessibility classes ---------------------------------------

  let uiScale = 1;
  function applySettings(s: Settings): void {
    uiScale = s.accessibility.uiScale / 100;
    document.documentElement.style.setProperty('--ui-scale', String(uiScale));
    root.classList.toggle('reduced-motion', s.gameplay.reducedMotion);
    root.classList.toggle('reduced-flashing', s.gameplay.reducedFlashing);
    root.classList.toggle('colorblind', s.accessibility.colorblind);
    applyViewport();
  }
  /**
   * Layout classes. At UI scale 100 % the desktop layouts always get ≥ 80 × 45 em (that is what
   * `--vp` keeps); a larger UI scale shrinks the room in em, so the compact (short) and narrow
   * layouts, built for phones down to 50 × 28 em, take over when the em-size calls for them.
   */
  function applyViewport(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const vp = viewportFactor(w, h);
    document.documentElement.style.setProperty('--vp', vp.toFixed(3));
    const em = BASE_FONT_PX * uiScale * vp;
    root.classList.toggle('compact', h < 400 || h / em < COMPACT_BELOW_EM);
    root.classList.toggle('portrait', h > w);
    root.classList.toggle('narrow', w < 560 || w / em < NARROW_BELOW_EM);
  }
  function applyDevice(): void {
    root.dataset.device = deps.services.input.device;
  }
  applySettings(deps.settings());
  applyViewport();
  applyDevice();
  base.bus.on('settings', applySettings);
  base.bus.on('input:device', applyDevice);
  window.addEventListener('resize', applyViewport);
  window.addEventListener('orientationchange', applyViewport);

  return {
    get screen() {
      return sm.current;
    },
    stack: () => sm.ids(),
    boot() {
      mode = 'boot';
      sm.replace('boot');
    },
    namePrompt() {
      sm.replace('name');
    },
    menu() {
      mode = 'menu';
      roomRoot = null;
      overlay.set('hidden');
      sm.replace('menu');
    },
    practice() {
      mode = 'practice';
      roomRoot = null;
      sm.replace('game');
    },
    room() {
      mode = 'room';
      roomRoot = null;
      followRoom();
    },
    error(props) {
      overlay.set('hidden');
      sm.replace('error', props);
    },
    connection(state, attempt) {
      overlay.set(state, attempt);
    },
    show(id, props) {
      sm.show(id, props);
    },
    update(view) {
      syncSpectator();
      // Content and viewport changes do not scroll: re-check the top screen's sheets 4× a second.
      const now = performance.now();
      if (now - fadeCheckAt > 250) {
        fadeCheckAt = now;
        const top = screens.get(sm.current)?.el;
        if (top) {
          for (const b of top.querySelectorAll<HTMLElement>('.sheet-body')) syncScrollFade(b);
          for (const t of top.querySelectorAll<HTMLElement>('.tabs')) syncTabFade(t);
        }
      }
      // A modal playing its exit is already closed: it gets no more updates.
      for (const s of screens.values()) if (!s.el.hidden && !s.el.classList.contains('closing')) s.update?.(view);
      captions.update();
      rotate.update(sm.current);
    },
  };
}
